/**
 * 颁奖夜（批 B）：一届金枢奖 / 星汉奖在界面上的两种形态。
 * 设计=docs/新增功能-年度奖项与网友评价.md §6 批 B
 *
 *  - **新闻版**（玩家未入围，13 届里的大多数）：一张网页新闻式结果卡——主角双奖当头条（大头像）、
 *    配角双奖作次条（一行），读完一个「知道了」就走。⛔ 报幕、⛔ 感言、⛔ VN：
 *    家里扫到一条颁奖新闻，不该占玩家十分钟。
 *  - **现场版**（玩家在任一类别入围）：凡是「听到的话」一律走 VnDialog——主持报幕 → 得主感言，
 *    按配角先行、主角压轴的揭晓顺序逐类播；玩家获奖那一类末尾出三个语气选项，选定即原位连播那句。
 *    VN 全部收起后才亮**同一张结果卡**（只多一行收益），⛔ 两版各做一套版式。
 *
 * 台词不落盘（往屆回看本批不做）：抽哪一条由「届次」算出的稳定序号决定 ⇒ 刷新页面⛔ 换词。
 * 接管屏幕的语义沿用月度执行：进 app.ts 的 MODAL_PANELS ⇒ 决策页留底、悬浮入口让位、
 * 小助理那段整段收起且不记已读（颁奖夜关掉后从头连播，「宁可重复不可漏话」的既有裁定）。
 */
import type { GameApp, Panel } from '../../app';
import { GameState } from '../../systems/GameState';
import { awardSystem } from '../../systems/AwardSystem';
import { GameData } from '../../data/GameData';
import { AwardCategory, AwardNominee, AwardRecord } from '../../data/types';
import { AWARD_MAP, AWARD_CATEGORY_ORDER, AwardId, CeremonyDef } from '../../data/tables/Awards';
import { PersonalityKey, PERSONALITY_KEYS, PERSONALITY_TEMPLATES } from '../../data/tables/NormalNpcData';
import {
    SpeechContext, TONE_LABELS, customWinnerSpeech, fallbackPersonality, hostInvitePlayer, hostNominate,
    hostVacant, hostWinner, playerSpeechChoices, winnerSpeech,
} from '../../data/tables/AwardSpeeches';
import { DialogLine, DialogSpeaker, VnDialog } from '../VnDialog';
import { createDiv, createSpan, createHr, createModalDismiss, clearChildren, createExecTopBar } from '../ui';
import { icon } from '../icons';
import { fillAvatarSlot, avatarPngSrc, AVATAR_MASTER_PX } from '../../avatar/gamemount';
import { npcAvatarConfig } from '../npcAvatar';

/** 按揭晓顺序的一届四类别（ordinal 同时是抽词的错位量，保证同届不撞句） */
interface Stage { rec: AwardRecord; ordinal: number; }

const HOST: DialogSpeaker = { id: 'host', name: '主持人', avatarChar: '主' };

export class AwardCeremonyPanel implements Panel {
    readonly element: HTMLElement;

    private readonly dimoverlay: HTMLElement;
    private readonly modalWrap: HTMLElement;
    private readonly card: HTMLElement;
    private readonly vn = new VnDialog();
    /** 现场版全屏场景（⌈2026-10-08 茄子令⌋颁奖夜=月度执行同款页面：换场景和文案） */
    private readonly scene: HTMLElement;
    private readonly sceneImg: HTMLImageElement;
    private readonly topBar = createExecTopBar();

    private awardId: AwardId = 'jingshu';
    private edition = 0;
    private def: CeremonyDef | null = null;
    private records: AwardRecord[] = [];

    constructor(private app: GameApp) {
        // 根：month-full 复用月度执行全套场景 CSS（scene/顶栏/文案位置）；新闻版弹窗时根保持透明
        const panel = createDiv('panel-root award-night-root month-full');
        this.dimoverlay = createDiv('dimoverlay');
        panel.appendChild(this.dimoverlay);

        // 现场版场景层+1:1 顶栏（仅 award-live 态显示；缺图回退容器纸白底）
        this.scene = createDiv('month-scene');
        this.sceneImg = document.createElement('img');
        this.sceneImg.className = 'month-scene-img';
        this.sceneImg.alt = '';
        this.sceneImg.onerror = () => { this.sceneImg.style.visibility = 'hidden'; };
        this.scene.appendChild(this.sceneImg);
        panel.appendChild(this.scene);
        // 顶栏直接挂根（同 MonthProgressPanel）：不要再套一层 `.exec-top-wrap`——
        // `.month-full .exec-top-wrap` 是按后代匹配的，套一层就等于把「14/18 内收 + 纸面 + 浮起阴影」量两遍，
        // 实测栏底从尺上的 102 掉到 116、纸面两侧各多收 18（茄子 2026-10-15 指「信息栏小了一圈」）
        panel.appendChild(this.topBar.root);

        this.modalWrap = createDiv('modal-wrap');
        this.card = createDiv('modal-note-card award-card');
        // 只读层：关闭热区＝wrap 内那颗透明按钮（2026-10-16 弹窗统一批·茄子令删掉卡内「知道了」），
        // 点它＝点空白＝关闭、Esc 走 modal.ts 现成的 .btn-cancel 分支，关掉即记「这届播过了」。
        // 用透明档而不是遮罩档＝现场版的结果卡坐在颁奖夜场景图上，refresh() 本来就显式撤着淡底，
        // 给它补一层 dim 会把现场版改样。
        this.modalWrap.appendChild(createModalDismiss(() => this.dismiss(), { painted: false }));
        this.modalWrap.appendChild(this.card);
        panel.appendChild(this.modalWrap);

        // 演出浮层压在本面板各层之上（z 档同月度弹窗内）
        panel.appendChild(this.vn.element);

        this.element = panel;
    }

    /** 进入即取「已结算未播出」的最早一届；没有就安静待着（本面板平时不显示） */
    refresh(): void {
        const target = awardSystem.unseenCeremony();
        if (!target) { this.closeLayers(); return; }
        this.awardId = target.awardId;
        this.edition = target.edition;
        this.def = awardSystem.ceremonyOf(target.awardId, target.edition) ?? null;
        this.records = awardSystem.recordsOf(target.awardId, target.edition);
        if (!this.records.length) { this.closeLayers(); return; }

        if (this.records.some(r => r.playerNominated || r.playerWon)) {
            // 现场版：全屏场景（颁奖夜图）+顶栏+VN 连播；结果卡最后亮在场景之上
            this.element.classList.add('award-live');
            this.topBar.update(GameState.getInstance());
            this.sceneImg.style.visibility = 'hidden';
            this.sceneImg.onload = () => { this.sceneImg.style.visibility = 'visible'; };
            this.sceneImg.src = 'scenes/bg-award.webp';
            this.dimoverlay.classList.remove('show');
            void this.startLive();
        } else {
            // 新闻版：决策页上的结果弹窗，原样保留
            this.element.classList.remove('award-live');
            this.sceneImg.style.visibility = 'hidden';
            this.dimoverlay.classList.add('show');
            this.showCard();
        }
    }

    destroy(): void {
        this.vn.destroy();
        this.closeLayers();
    }

    // ===== 现场版：逐类别连播 =====

    private async startLive(): Promise<void> {
        const stages = AWARD_CATEGORY_ORDER
            .map(cat => this.records.find(r => r.category === cat))
            .filter((r): r is AwardRecord => !!r)
            .map((rec, ordinal) => ({ rec, ordinal }));
        const speakers = await this.buildSpeakers(stages);
        this.playStage(stages, speakers, 0);
    }

    private playStage(stages: Stage[], speakers: Map<string, DialogSpeaker>, i: number): void {
        if (i >= stages.length) { this.showCard(); return; }
        this.vn.play(this.linesFor(stages[i], speakers), () => this.playStage(stages, speakers, i + 1));
    }

    private linesFor(stage: Stage, speakers: Map<string, DialogSpeaker>): DialogLine[] {
        const { rec, ordinal } = stage;
        const ctx = this.ctxOf(rec);
        if (rec.vacant) return [{ speaker: HOST, text: hostVacant(ctx) }];

        const winner = rec.nominees[0];
        const lines: DialogLine[] = [];
        // 只有玩家入围的那一类才念全名单；其余类别观众只关心得主是谁
        if (rec.playerNominated) lines.push({ speaker: HOST, text: hostNominate(ctx, rec.nominees) });

        if (rec.playerWon) {
            // 分支只在整段末句生效 ⇒ 每个类别各播一段；选项文字给语气标签，选中才念出台词
            lines.push({
                speaker: HOST,
                text: hostWinner(ctx),
            });
            lines.push({
                speaker: HOST,
                text: hostInvitePlayer(ctx),
                choices: playerSpeechChoices(GameState.getInstance().player.rank).map(c => ({
                    text: TONE_LABELS[c.tone],
                    reply: { speaker: speakers.get(winner.performer) ?? HOST, text: c.text },
                })),
            });
            return lines;
        }

        lines.push({ speaker: HOST, text: hostWinner(ctx) });
        // 特殊 NPC 写过专属感言就优先念它（占位符取不到值会自动回落）；
        // 随机 NPC 与剧情届得主没有 NPCDef，照旧走四性格通用池。
        const custom = customWinnerSpeech(GameData.NPC_MAP[winner.performer]?.awardSpeechText, ctx);
        const speech = custom
            || winnerSpeech(this.personalityOf(winner), this.awardId, this.edition, ordinal, ctx);
        if (speech) lines.push({ speaker: speakers.get(winner.performer) ?? HOST, text: speech });
        return lines;
    }

    /** 得主说话人：普通 NPC 有捏脸上图（同 67 批口径），剧情届与兜底走首字色块 */
    private async buildSpeakers(stages: Stage[]): Promise<Map<string, DialogSpeaker>> {
        const player = GameState.getInstance().player;
        const out = new Map<string, DialogSpeaker>();
        const jobs: Promise<void>[] = [];
        for (const { rec } of stages) {
            const w = rec.nominees[0];
            if (!w || out.has(w.performer)) continue;
            const sp: DialogSpeaker = { id: w.performer, name: w.name, avatarChar: w.name.slice(0, 1) };
            out.set(w.performer, sp);
            const cfg = npcAvatarConfig(w.performer);   // 主角＝当前穿着那张脸；随机 NPC＝档内实例；固定 NPC＝主表定稿
            if (cfg) {
                jobs.push(avatarPngSrc(cfg, AVATAR_MASTER_PX)
                    .then(src => { sp.avatarSrc = src; })
                    .catch(() => { /* 出图失败就留首字色块，⛔ 卡住颁奖 */ }));
            }
        }
        await Promise.all(jobs);
        return out;
    }

    // ===== 结果卡（两版共用） =====

    private showCard(): void {
        this.renderCard();
        this.modalWrap.classList.add('show');
    }

    private renderCard(): void {
        const award = AWARD_MAP[this.awardId];
        clearChildren(this.card);

        const title = createDiv('mtitle', [`${award.name} · 第${this.edition} 届`]);
        title.insertBefore(icon('trophy'), title.firstChild);
        this.card.appendChild(title);
        this.card.appendChild(createSpan('award-when',
            `第${this.records[0].year + 1} 年${this.def?.month ?? 1}月`));
        this.card.appendChild(createHr());

        // 头条＝主角双奖（女主在左），次条＝配角双奖：网页新闻的层级，⛔ 四条等宽
        const lead = createDiv('award-lead');
        lead.appendChild(this.leadItem(award.categoryNames.bestActress, 'bestActress'));
        lead.appendChild(this.leadItem(award.categoryNames.bestActor, 'bestActor'));
        this.card.appendChild(lead);
        this.card.appendChild(createHr());
        this.card.appendChild(this.subRow(award.categoryNames.bestSupportingActress, 'bestSupportingActress'));
        this.card.appendChild(this.subRow(award.categoryNames.bestSupportingActor, 'bestSupportingActor'));

        const gain = this.gainLine();
        if (gain) this.card.appendChild(gain);

        // 2026-10-16 弹窗统一批（茄子令）：卡内这颗「知道了」删掉，关闭热区在 wrap 上（见构造函数）
    }

    private leadItem(categoryName: string, category: AwardCategory): HTMLElement {
        const box = createDiv('award-lead-item');
        box.appendChild(createDiv('award-cat', [categoryName]));
        const w = this.winnerOf(category);
        if (!w) {
            box.classList.add('vacant');
            box.appendChild(createDiv('award-vacant', ['本届从缺']));
            return box;
        }
        const slot = createDiv('npc-avatar award-avatar');
        fillAvatarSlot(slot, this.emojiOf(w), this.avatarOf(w));
        box.appendChild(slot);
        const name = createDiv('award-name', [w.name]);
        if (w.performer === 'player') name.appendChild(createSpan('award-you', '你'));
        box.appendChild(name);
        box.appendChild(createDiv('award-work', [`《${w.workName}》`]));
        return box;
    }

    private subRow(categoryName: string, category: AwardCategory): HTMLElement {
        const row = createDiv('award-sub-row');
        const w = this.winnerOf(category);
        row.appendChild(createSpan('award-cat', categoryName));
        row.appendChild(createSpan(w ? 'award-sub-winner' : 'award-vacant',
            w ? `${w.name}《${w.workName}》` : '本届从缺'));
        return row;
    }

    /** 玩家本届收益行（数值由 AwardSystem 按同一套复获系数算，UI ⛔ 自己乘） */
    private gainLine(): HTMLElement | null {
        const payout = awardSystem.payoutOfCeremony(this.awardId, this.edition);
        if (!payout) return null;
        const line = createDiv('award-gain');
        line.appendChild(createSpan('award-gain-text', `名气 +${payout.fame} · 口碑 +${payout.rep}`));
        for (const id of payout.tagIds) {
            const tag = GameData.TAG_MAP[id];
            if (tag) line.appendChild(createSpan('award-title-chip', `「${tag.name}」`));
        }
        return line;
    }

    // ===== 取值小件 =====

    private winnerOf(category: AwardCategory): AwardNominee | null {
        const rec = this.records.find(r => r.category === category);
        return rec && !rec.vacant ? rec.nominees[0] : null;
    }

    private ctxOf(rec: AwardRecord): SpeechContext {
        const award = AWARD_MAP[this.awardId];
        const w = rec.nominees[0];
        // %角色名% 要的是剧中角色本名（回表 roles[roleIndex].name，同 EndingSystem 获奖陈列口径），
        // ⛔ 角色定位——2026-10-20 实机抓虫：「演 男主角 那半年」。拿不到本名（剧情届 scriptId 空 /
        // 剧本下架 / 综艺无名位）就留空串，含 %角色名% 的句子按「缺数据整句不抽」口径自动出局。
        // 固定阵容（roleIndex = -1）没有 roles 位，名字在提名快照 roleName 里（2026-10-21）。
        const script = w?.scriptId ? GameData.SCRIPT_MAP[w.scriptId] : undefined;
        const roleName = w?.roleName
            || (script && w && w.roleIndex >= 0 ? script.roles[w.roleIndex]?.name ?? '' : '');
        return {
            awardName: award.name,
            edition: this.edition,
            categoryName: award.categoryNames[rec.category],
            winnerName: w?.name ?? '',
            workName: w?.workName ?? '',
            roleName,
            rankName: w ? awardSystem.rankName(w.rank, w.gender) : '',
        };
    }

    private npcOf(performer: string) {
        return GameState.getInstance().player.normalNpcs?.find(n => n.id === performer);
    }

    private avatarOf(w: AwardNominee) {
        return npcAvatarConfig(w.performer);
    }

    /** 头像兜底字：普通 NPC 用性格 emoji，玩家用立绘 emoji，剧情届得主取首字 */
    private emojiOf(w: AwardNominee): string {
        if (w.performer === 'player') return '👩‍🎤';
        const npc = this.npcOf(w.performer);
        if (npc) return PERSONALITY_TEMPLATES[npc.personality as PersonalityKey]?.emoji ?? w.name.slice(0, 1);
        return w.name.slice(0, 1);
    }

    private personalityOf(w: AwardNominee): PersonalityKey {
        const npc = this.npcOf(w.performer);
        // 竞争型特殊 NPC 的实例带 personality='special'（Social/Encounter 走 NPCDef 专属池的标记），
        // 它不在 NPC_SPEECHES 的四性格池里。早期实现直接强转 PersonalityKey：得主是特殊 NPC 时
        // winnerSpeech 取到 undefined 再 .filter 抛 TypeError，现场版演到那一类就整场卡死
        //（2026-10-20 实机：第 4 年 1 月金枢 24，罗一鸣获女配）。
        // 没有专属颁奖台词的特殊/剧情届得主一律按名字稳定映射到四性格，与刷新不换词同口径。
        if (npc && (PERSONALITY_KEYS as string[]).includes(npc.personality)) {
            return npc.personality as PersonalityKey;
        }
        return fallbackPersonality(w.name);
    }

    private closeLayers(): void {
        this.dimoverlay.classList.remove('show');
        this.modalWrap.classList.remove('show');
    }

    /** 关掉才算播过：中途换页/刷新没点「知道了」，下次进决策日会补播 */
    private dismiss(): void {
        this.vn.stop();
        awardSystem.markCeremonySeen(this.awardId, this.edition);
        this.closeLayers();
        this.app.show('decision');
    }
}
